// Free-tier SMTP provider presets with one-click auto-fill + in-panel tutorials.
// Each preset includes: free quota, host/port, signup URL, step-by-step setup.

export const SMTP_PRESETS = [
  {
    id: 'brevo',
    name: 'Brevo (Sendinblue)',
    tag: 'RECOMMENDED',
    free: '300 emails / day — free forever',
    bestFor: 'Best free reliability for transactional + marketing email.',
    host: 'smtp-relay.brevo.com',
    port: '587',
    useTls: 'true',
    signupUrl: 'https://www.brevo.com/free-smtp-server/',
    docUrl: 'https://help.brevo.com/hc/en-us/articles/7924908994450-Send-transactional-emails-using-Brevo-SMTP',
    setupMinutes: 5,
    steps: [
      'Sign up free at brevo.com — no credit card required.',
      'In the dashboard go to: SMTP & API → SMTP tab.',
      'Click "Generate a new SMTP key" — give it a name like "Masterpiece Tools".',
      'Copy the SMTP login (looks like yourname@smtp-brevo.com) into "Username" above.',
      'Copy the SMTP key into "Password" above.',
      'Host and port are already filled in (smtp-relay.brevo.com:587).',
      'Press "Save", then "Send test". You should receive the test email within seconds.',
    ],
  },
  {
    id: 'gmail',
    name: 'Gmail',
    tag: 'EASIEST',
    free: '500 emails / day from your Gmail account',
    bestFor: 'Easiest setup if you already use Gmail. Lower limits than Brevo.',
    host: 'smtp.gmail.com',
    port: '587',
    useTls: 'true',
    signupUrl: 'https://accounts.google.com',
    docUrl: 'https://support.google.com/accounts/answer/185833',
    setupMinutes: 3,
    steps: [
      'Sign in to your Gmail account.',
      'Enable 2-Step Verification (required): myaccount.google.com → Security → 2-Step Verification → Turn on.',
      'Once 2-Step is on, open: myaccount.google.com/apppasswords',
      'Type a name like "Masterpiece Tools" and press Create.',
      'Google shows a 16-character password (e.g. abcd efgh ijkl mnop). Copy it.',
      'Paste your Gmail address into "Username" and the 16-char password into "Password" above.',
      'Press "Save", then "Send test". The first email might land in Spam — mark it "Not spam".',
    ],
  },
  {
    id: 'resend',
    name: 'Resend',
    tag: 'DEV-FRIENDLY',
    free: '100 emails / day · 3,000 / month',
    bestFor: 'Modern, beautiful logs + analytics. Easiest if you have a custom domain.',
    host: 'smtp.resend.com',
    port: '465',
    useTls: 'true',
    signupUrl: 'https://resend.com/signup',
    docUrl: 'https://resend.com/docs/send-with-smtp',
    setupMinutes: 5,
    steps: [
      'Sign up free at resend.com.',
      'Add and verify your domain (Settings → Domains) — Resend gives you a few DNS records.',
      'Go to: API Keys → Create API Key → name it "Masterpiece Tools".',
      'Copy the API key (starts with re_).',
      'In "Username" type exactly: resend',
      'In "Password" paste the API key.',
      'Host and port are already filled in (smtp.resend.com:465).',
      'Press "Save", then "Send test".',
    ],
  },
  {
    id: 'sendgrid',
    name: 'SendGrid',
    tag: 'ENTERPRISE',
    free: '100 emails / day forever',
    bestFor: 'Industry standard with deep analytics. Owned by Twilio.',
    host: 'smtp.sendgrid.net',
    port: '587',
    useTls: 'true',
    signupUrl: 'https://signup.sendgrid.com/',
    docUrl: 'https://docs.sendgrid.com/for-developers/sending-email/integrating-with-the-smtp-api',
    setupMinutes: 8,
    steps: [
      'Sign up free at sendgrid.com (verify your email first).',
      'Complete Single Sender Verification: Settings → Sender Authentication → Verify a Single Sender.',
      'Create an API key: Settings → API Keys → Create API Key → Restricted Access → only enable "Mail Send".',
      'Copy the API key (only shown once).',
      'In "Username" type exactly: apikey  (yes, literally the word apikey)',
      'In "Password" paste the API key (starts with SG.).',
      'Press "Save", then "Send test".',
    ],
  },
  {
    id: 'mailjet',
    name: 'Mailjet',
    tag: 'GENEROUS',
    free: '200 emails / day · 6,000 / month',
    bestFor: 'Highest daily limit on free tier; EU-based servers.',
    host: 'in-v3.mailjet.com',
    port: '587',
    useTls: 'true',
    signupUrl: 'https://app.mailjet.com/signup',
    docUrl: 'https://documentation.mailjet.com/hc/en-us/articles/360043229473-About-SMTP-Relay',
    setupMinutes: 5,
    steps: [
      'Sign up free at mailjet.com.',
      'Go to: Account → SMTP and SEND API Settings.',
      'Copy your "API Key" — paste into "Username" above.',
      'Copy your "Secret Key" — paste into "Password" above.',
      'Host and port are already filled in (in-v3.mailjet.com:587).',
      'Verify a sender address: Senders & Domains → Add a sender.',
      'Press "Save", then "Send test".',
    ],
  },
  {
    id: 'mailtrap',
    name: 'Mailtrap',
    tag: 'TESTING',
    free: '1,000 emails / month free for sending',
    bestFor: 'Best if you only want to test layouts without emails actually leaving.',
    host: 'send.smtp.mailtrap.io',
    port: '587',
    useTls: 'true',
    signupUrl: 'https://mailtrap.io/register/signup',
    docUrl: 'https://help.mailtrap.io/article/96-mailtrap-sending-quickstart',
    setupMinutes: 4,
    steps: [
      'Sign up free at mailtrap.io.',
      'Switch to the "Email Sending" product (top-left menu).',
      'Add and verify your sending domain (or use the demo subdomain).',
      'Open: Sending → SMTP/API Settings.',
      'Copy the SMTP username + password and paste them above.',
      'Host and port are already filled in.',
      'Press "Save", then "Send test".',
    ],
  },
  {
    id: 'outlook',
    name: 'Outlook / Office 365',
    tag: 'MICROSOFT',
    free: 'Free with any Microsoft 365 / Outlook.com account',
    bestFor: 'Use your existing Microsoft work account.',
    host: 'smtp.office365.com',
    port: '587',
    useTls: 'true',
    signupUrl: 'https://outlook.live.com',
    docUrl: 'https://learn.microsoft.com/en-us/exchange/clients-and-mobile-in-exchange-online/authenticated-client-smtp-submission',
    setupMinutes: 5,
    steps: [
      'Sign in to your Outlook / Microsoft 365 account.',
      'Make sure SMTP AUTH is enabled (admin.exchange.microsoft.com → Recipients → Mailboxes → your account → Mail flow settings).',
      'In "Username" type your full Outlook email address.',
      'In "Password" type your account password (or App Password if 2-Step is on: account.microsoft.com → Security → App passwords).',
      'Host and port are already filled in (smtp.office365.com:587).',
      'Press "Save", then "Send test".',
    ],
  },
  {
    id: 'godaddy',
    name: 'GoDaddy Professional Email',
    tag: 'DOMAIN MAIL',
    free: 'Included with GoDaddy email plans (paid, but you may already own it with your domain)',
    bestFor: 'You bought email with your domain at GoDaddy — modern Microsoft-365-powered mailbox.',
    host: 'smtp.office365.com',
    port: '587',
    useTls: 'true',
    signupUrl: 'https://www.godaddy.com/email/professional-business-email',
    docUrl: 'https://www.godaddy.com/help/server-and-port-settings-for-workspace-email-6949',
    setupMinutes: 4,
    steps: [
      'Sign in to email.godaddy.com with your GoDaddy email account (e.g. yourname@yourdomain.com).',
      'If you have the new "Professional Email" plan (Microsoft-365-based — this is the default since 2024), use the settings already filled in (smtp.office365.com : 587, STARTTLS).',
      'In "Username" type your full GoDaddy email address (yourname@yourdomain.com).',
      'In "Password" type your mailbox password. If 2-Step Verification is on, create an App Password at account.microsoft.com → Security → Advanced security options → App passwords.',
      'Make sure SMTP AUTH is enabled in your account (it usually is by default).',
      'Press "Save", then "Send test".',
      'If sending fails, switch to the GoDaddy Workspace preset below — your mailbox might still be on the legacy platform.',
    ],
  },
  {
    id: 'godaddy-workspace',
    name: 'GoDaddy Workspace Email (legacy)',
    tag: 'DOMAIN MAIL',
    free: 'Included with legacy GoDaddy Workspace Email plans',
    bestFor: 'Legacy GoDaddy Workspace mailboxes (created before 2024).',
    host: 'smtpout.secureserver.net',
    port: '465',
    useTls: 'true',
    signupUrl: 'https://www.godaddy.com/email',
    docUrl: 'https://www.godaddy.com/help/server-and-port-settings-for-workspace-email-6949',
    setupMinutes: 4,
    steps: [
      'Sign in at email.godaddy.com using your Workspace Email account.',
      'Host and port are already filled in: smtpout.secureserver.net : 465 (SSL).',
      'In "Username" type your full Workspace email address (e.g. you@yourdomain.com).',
      'In "Password" type the mailbox password set in the GoDaddy Email Control Center.',
      'If port 465 is blocked on your network, try 3535 with TLS enabled, or 587 with TLS.',
      'Press "Save", then "Send test".',
      'Limits: roughly 250 outbound messages / 24 h per mailbox on Workspace plans.',
    ],
  },
  {
    id: 'custom',
    name: 'Other / Custom SMTP',
    tag: 'CUSTOM',
    free: 'Use any SMTP host you already have',
    bestFor: 'Already-owned cPanel / Zoho / Yandex / Postmark / SES etc.',
    host: '',
    port: '587',
    useTls: 'true',
    signupUrl: '',
    docUrl: '',
    setupMinutes: 3,
    steps: [
      'Fill in your provider\'s SMTP host (e.g. smtp.yourdomain.com).',
      'Port is usually 587 (STARTTLS) or 465 (SSL).',
      'Username = the full email address you want to send from (most providers).',
      'Password = the mailbox password or App Password.',
      'Press "Save", then "Send test".',
    ],
  },
];
